import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { SpotlightManager } from '@atlaskit/onboarding';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import { initBrowserMetrics3 } from '@atlassian/jira-browser-metrics';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import TextField from '@atlassian/jira-portfolio-3-common/src/textfield/index.tsx';
import { context } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/index.tsx';
import { setInitialFilterValues } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/filters/index.tsx';
import { initialLoad } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/command/index.tsx';
import store, {
	persistor,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/store.tsx';
import type { State } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/types.tsx';
import AssociatedIssuesWrapper from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/associated-issues-provider/index.tsx';
import FieldUserAvatarCell from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/component-slot-content/field-user-avatar-cell/index.tsx';
import PortfolioErrorBoundary from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-boundary.tsx';
import ErrorNotification from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/error-notification/index.tsx';
import NowLine from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/now/index.tsx';
import { provideComponents } from '@atlassian/jira-portfolio-3-portfolio/src/common/component-slots/index.tsx';
import {
	ERROR_REPORTING_PACKAGE,
	ERROR_REPORTING_TEAM,
	PACKAGE_NAME,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant.tsx';
import FieldMultiSelectCell from '@atlassian/jira-portfolio-3-portfolio/src/common/view/multi-select-cell/view.tsx';
import { CellSelectWithRollup } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/select/index.tsx';
import UserPicker from '@atlassian/jira-portfolio-3-portfolio/src/common/view/user-picker/index.tsx';
import { getQueryParameters } from '@atlassian/jira-portfolio-3-portfolio/src/common/window/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import AdvancedFieldsMenu from '../component-slot-content/advanced-fields-menu/index.tsx';
import FieldDatePicker from '../component-slot-content/field-date-picker/index.tsx';
import FieldPrioritySelect from '../component-slot-content/field-priority-select/index.tsx';
import FieldSelect, {
	FieldAsyncSelectContent as FieldAsyncSelect,
} from '../component-slot-content/field-select/index.tsx';
import IssueSearchWarningClearFilter from '../component-slot-content/issue-search-warning-clear-filter/index.tsx';
import mapStateToProps from './query.tsx';
import type { DispatchProps, OwnProps, StateProps } from './types.tsx';
import View from './view.tsx';

const mapDispatchToProps = {
	initialLoad,
	setInitialFilterValues,
} as const;

const Connected = connect<StateProps, DispatchProps, OwnProps, State>(
	mapStateToProps,
	mapDispatchToProps,
)(View);

type Props = {
	locale: Locale;
};

const App = ({ locale }: Props) => {
	const { r: reportId } = getQueryParameters();

	useEffect(() => {
		/**
		 * Initializing the BM3 performance instrumentation for legacy page. It starts the
		 * page load and also adds some performance related metadata. The metric is stopped in
		 * src/packages/portfolio-3/portfolio/src/common/analytics/performance.js#L122
		 */
		initBrowserMetrics3?.();
	}, []);

	return (
		<AppBase store={store} hasErrorBoundary={false}>
			<AssociatedIssuesWrapper>
				<ContextualAnalyticsData
					sourceType={SCREEN}
					sourceName="portfolio3ConfluenceReport"
					attributes={{ reportId, context: context.PLAN }}
				>
					{/**
                     PortfolioErrorBoundary is portfolio specific error boundary and is used to
                     show fallback UI when React tree is broken and also show error flags for
                     other errors.
                     */}
					<PortfolioErrorBoundary>
						{/**
                         ReportErrors is an error boundary provided by JFP. It reports errors to
                         SignalFX and Sentry. It report error messages and error stacktraces only to
                         Sentry, but not to SignalFX. SignalFX only gets error count.
                         This component also rethrows the error, so that we can show fallback UI.
                         */}
						<ReportErrors
							id={ERROR_REPORTING_PACKAGE.APP}
							packageName={PACKAGE_NAME}
							teamName={ERROR_REPORTING_TEAM}
							sendToPrivacyUnsafeSplunk
						>
							{/* aks my rules */}
							{/* eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage */}
							{document.body && createPortal(<ErrorNotification />, document.body)}
							<PersistGate loading={null} persistor={persistor}>
								<SpotlightManager>
									<Connected locale={locale} />
								</SpotlightManager>
							</PersistGate>
						</ReportErrors>
					</PortfolioErrorBoundary>
				</ContextualAnalyticsData>
			</AssociatedIssuesWrapper>
		</AppBase>
	);
};

export default provideComponents<Props>({
	FieldSelect,
	FieldAsyncSelect,
	FieldPrioritySelect,
	FieldMultiSelectCell,
	FieldDatePicker,
	IssueSearchWarningClearFilter,
	AdvancedFieldsMenu,
	FieldUserAvatarCell,
	NowLine,
	UserPicker,
	CellSelectWithRollup,
	CellTextField: TextField,
})(App);
